// ABOUTME: Schedule + publish-targets card. Profile picker is a row of brand
// avatar pills (postiz-style); incompatible platforms render disabled with a
// tooltip explaining why. Default selection only includes platforms that can
// actually publish the given contentType — no more auto-checking YouTube on
// a text post and shipping a guaranteed validation error.

import { useState, useCallback, useMemo, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { CHAT_CARD } from '@/components/chat/chatCard'
import { todayLocalDateString } from '@/lib/dateUtils'
import { getPlatformBrand, platformCompatibility } from '@/lib/platforms'
import { PlatformCustomizer } from './PlatformCustomizer'
import type { PlatformOverrideData } from './PlatformTab'

interface SocialProfile {
  id: string
  name: string
  platform: string
  handle: string
}

export interface PlatformOverrides {
  base_body: string
  overrides: Record<string, PlatformOverrideData>
}

interface SchedulingCardProps {
  profiles: SocialProfile[]
  noAccounts?: boolean
  mediaUrl?: string
  /** Drives default-selection: profiles that can't post this type are unchecked + disabled. */
  contentType?: string
  format?: string | null
  hasMedia?: boolean
  onSchedule: (
    scheduledAt: string | null,
    profileIds: string[],
    platformOverrides?: PlatformOverrides
  ) => void
  onCancel: () => void
  /**
   * Whether the "Customize per Platform" flow is offered. Defaults to true.
   * The overrides this flow produces are only persisted by consumers whose
   * `onSchedule` handles the third argument — set this to false for any
   * consumer that would otherwise silently drop the customized copy.
   */
  allowPlatformCustomization?: boolean
}

type ScheduleOption = 'now' | 'later' | 'custom'

function getTodayLaterTime(): string {
  const d = new Date()
  d.setHours(d.getHours() + 2, 0, 0, 0)
  return d.toTimeString().slice(0, 5)
}

export function SchedulingCard({
  profiles,
  noAccounts,
  mediaUrl,
  contentType,
  format = null,
  hasMedia,
  onSchedule,
  onCancel,
  allowPlatformCustomization = true,
}: SchedulingCardProps) {
  const [scheduleOption, setScheduleOption] = useState<ScheduleOption>('now')
  const [laterTime, setLaterTime] = useState<string>(getTodayLaterTime())
  const [customDate, setCustomDate] = useState<string>(todayLocalDateString())
  const [customTime, setCustomTime] = useState<string>('09:00')
  const [showCustomizer, setShowCustomizer] = useState(false)

  // Per-profile compatibility. When contentType isn't provided (chat flow),
  // skip filtering and treat everything as compatible.
  const profilesWithCompat = useMemo(() => {
    return profiles.map((p) => ({
      profile: p,
      compat: contentType
        ? platformCompatibility(p.platform, contentType, format, !!hasMedia)
        : { compatible: true as const },
    }))
  }, [profiles, contentType, format, hasMedia])

  const compatibleIds = useMemo(
    () => profilesWithCompat.filter((p) => p.compat.compatible).map((p) => p.profile.id),
    [profilesWithCompat]
  )

  const [selectedProfileIds, setSelectedProfileIds] = useState<string[]>(compatibleIds)

  // Re-seed selection if the profile list / compatibility changes (e.g.,
  // contentType changes upstream). Only auto-select on first load to avoid
  // stomping user toggles.
  const [hasUserToggled, setHasUserToggled] = useState(false)
  useEffect(() => {
    if (!hasUserToggled) setSelectedProfileIds(compatibleIds)
  }, [compatibleIds, hasUserToggled])

  function toggleProfile(id: string) {
    setHasUserToggled(true)
    setSelectedProfileIds((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    )
  }

  function buildScheduledAt(): string | null {
    if (scheduleOption === 'now') return null
    if (scheduleOption === 'later') {
      const [h, m] = laterTime.split(':').map(Number)
      const d = new Date()
      d.setHours(h, m, 0, 0)
      return d.toISOString()
    }
    return new Date(`${customDate}T${customTime}`).toISOString()
  }

  function handleSubmit() {
    onSchedule(buildScheduledAt(), selectedProfileIds)
  }

  const selectedPlatforms = [
    ...new Set(profiles.filter((p) => selectedProfileIds.includes(p.id)).map((p) => p.platform)),
  ]

  const canCustomize = allowPlatformCustomization && selectedPlatforms.length >= 2

  const handleCustomizerSubmit = useCallback(
    (overrides: Record<string, PlatformOverrideData>) => {
      const scheduledAt = buildScheduledAt()
      onSchedule(scheduledAt, selectedProfileIds, { base_body: '', overrides })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedProfileIds, onSchedule, scheduleOption, laterTime, customDate, customTime]
  )

  const handleCustomizerCancel = useCallback(() => {
    setShowCustomizer(false)
  }, [])

  const blockedCount = profilesWithCompat.filter((p) => !p.compat.compatible).length

  if (showCustomizer) {
    return (
      <div className={cn(CHAT_CARD, 'space-y-2')}>
        <h3 className="text-sm font-medium">Customize per Platform</h3>
        <PlatformCustomizer
          platforms={selectedPlatforms}
          baseBody=""
          mediaUrl={mediaUrl}
          onSubmit={handleCustomizerSubmit}
          onCancel={handleCustomizerCancel}
        />
      </div>
    )
  }

  const hasAccounts = !noAccounts && profiles.length > 0
  const showTimeInput = scheduleOption !== 'now'

  return (
    <div className={cn(CHAT_CARD, 'space-y-2')} data-testid="scheduling-card">
      {/* Row 1 — title + counter */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">Schedule Post</h3>
        {hasAccounts && (
          <span className="text-muted-foreground text-xs">
            {selectedProfileIds.length}/{profiles.length} selected
            {blockedCount > 0 && ` · ${blockedCount} unsupported`}
          </span>
        )}
      </div>

      {/* Row 2 — When buttons + time inputs + actions */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1">
          {(['now', 'later', 'custom'] as ScheduleOption[]).map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => setScheduleOption(opt)}
              className={cn(
                'rounded-md border px-2 py-1 text-xs font-medium transition-colors',
                scheduleOption === opt
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-background text-muted-foreground hover:bg-accent'
              )}
            >
              {opt === 'now' ? 'Now' : opt === 'later' ? 'Later' : 'Custom'}
            </button>
          ))}
        </div>

        {showTimeInput && scheduleOption === 'later' && (
          <input
            type="time"
            aria-label="Time today"
            value={laterTime}
            onChange={(e) => setLaterTime(e.target.value)}
            className="border-border bg-background rounded border px-2 py-1 text-xs"
          />
        )}
        {showTimeInput && scheduleOption === 'custom' && (
          <>
            <input
              type="date"
              value={customDate}
              onChange={(e) => setCustomDate(e.target.value)}
              className="border-border bg-background rounded border px-2 py-1 text-xs"
            />
            <input
              type="time"
              value={customTime}
              onChange={(e) => setCustomTime(e.target.value)}
              className="border-border bg-background rounded border px-2 py-1 text-xs"
            />
          </>
        )}

        <div className="ml-auto flex items-center gap-1">
          {canCustomize && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowCustomizer(true)}
              className="h-7 px-2 text-xs"
              data-testid="customize-per-platform-btn"
            >
              Customize ({selectedPlatforms.length})
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onCancel} className="h-7 px-2 text-xs">
            Cancel
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onSchedule(null, [])}
            className="h-7 px-2 text-xs"
          >
            Save as draft
          </Button>
          {hasAccounts && (
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={selectedProfileIds.length === 0}
              className="h-7 px-2 text-xs"
            >
              {scheduleOption === 'now' ? 'Post now' : 'Schedule'}
            </Button>
          )}
        </div>
      </div>

      {/* Row 3 — platform pills */}
      {hasAccounts && (
        <div className="flex flex-wrap gap-1 pt-1">
          {profilesWithCompat.map(({ profile, compat }) => {
            const brand = getPlatformBrand(profile.platform)
            const Icon = brand.icon
            const isSelected = selectedProfileIds.includes(profile.id)
            const isBlocked = !compat.compatible
            const tooltip = isBlocked
              ? compat.blockedReason
              : (compat.warning ?? `${brand.label} — ${profile.handle}`)

            return (
              <button
                key={profile.id}
                type="button"
                onClick={() => !isBlocked && toggleProfile(profile.id)}
                disabled={isBlocked}
                title={tooltip}
                aria-label={`${brand.label} ${profile.handle}${isBlocked ? ` (disabled: ${compat.blockedReason})` : ''}`}
                aria-pressed={isSelected}
                data-testid={`platform-pill-${profile.platform.toLowerCase()}`}
                className={cn(
                  'group relative flex w-[60px] flex-col items-center gap-0.5 rounded border px-1 py-1 transition-all',
                  isBlocked
                    ? 'border-muted-foreground/20 cursor-not-allowed border-dashed opacity-40'
                    : 'hover:border-primary/40 cursor-pointer',
                  isSelected && !isBlocked ? 'border-primary bg-primary/10' : 'border-border'
                )}
              >
                <div
                  className={cn(
                    'flex size-6 items-center justify-center rounded-full',
                    brand.bg,
                    brand.fg,
                    !isSelected && !isBlocked && 'opacity-60 group-hover:opacity-100'
                  )}
                >
                  <Icon className="size-3" strokeWidth={2.5} />
                </div>
                <span className="text-muted-foreground w-full truncate text-center text-[0.625rem] leading-tight">
                  {profile.handle}
                </span>
                {compat.warning && !isBlocked && (
                  <span className="ring-background bg-warning absolute -top-0.5 -right-0.5 size-1.5 rounded-full ring-2" />
                )}
              </button>
            )
          })}
        </div>
      )}

      {!hasAccounts && (
        <div
          data-testid="scheduling-no-accounts"
          className="text-foreground border-warning/30 bg-warning/10 rounded border px-3 py-2 text-xs"
        >
          <p className="font-medium">You haven&apos;t connected any social accounts yet.</p>
          <p className="text-muted-foreground mt-0.5">
            Connect at least one account so MaxFlow can publish for you.{' '}
            <a
              href="/settings/accounts"
              className="text-primary font-medium underline underline-offset-2"
              data-testid="scheduling-connect-link"
            >
              Connect now →
            </a>
          </p>
        </div>
      )}
    </div>
  )
}
